// Migratielaag voor scenario-JSON bij import.
//
// Elk geëxporteerd scenario bevat `schemaVersion`. Bij import accepteren
// we oudere versies en upgraden ze stap-voor-stap naar
// CURRENT_SCHEMA_VERSION. Per versie-overgang voegen we een functie toe;
// voor v1 is er nog niets te migreren — alleen validatie.

import {
  CURRENT_SCHEMA_VERSION,
  type Block,
  type BlockType,
  type Scenario,
} from "../types/scenario";

const VALID_TYPES: BlockType[] = ["gebouw", "tuin", "bestrating", "boomstam", "boombladdek"];

interface ValidationResult {
  ok: boolean;
  error?: string;
  scenario?: Scenario;
}

/**
 * Valideer en upgrade een opgegeven object naar een actueel scenario.
 * Geeft `{ ok: false, error }` bij een serieuze structuurfout.
 */
export function migrateAndValidate(input: unknown): ValidationResult {
  if (!input || typeof input !== "object") {
    return { ok: false, error: "Geen geldig JSON-object." };
  }
  const obj = input as Record<string, unknown>;

  let version = typeof obj.schemaVersion === "number" ? obj.schemaVersion : 0;
  if (version > CURRENT_SCHEMA_VERSION) {
    return {
      ok: false,
      error: `Onbekende schemaversie ${version}. Upgrade de Schaduwtool of exporteer opnieuw vanuit een oudere bron.`,
    };
  }

  // Toekomstige migratiestappen kun je hier toevoegen, bv:
  //   if (version < 2) { migrateFromV1ToV2(obj); version = 2; }
  if (version < 1) {
    // Geen v0 in productie geweest; behandel als upgrade naar v1.
    obj.schemaVersion = CURRENT_SCHEMA_VERSION;
    version = CURRENT_SCHEMA_VERSION;
  }

  // Structuurvalidatie van het v1-schema.
  if (typeof obj.id !== "string" || obj.id.length === 0) {
    return { ok: false, error: "Veld 'id' ontbreekt of is leeg." };
  }
  if (typeof obj.name !== "string") {
    return { ok: false, error: "Veld 'name' ontbreekt." };
  }
  if (!obj.view || typeof obj.view !== "object") {
    return { ok: false, error: "Veld 'view' ontbreekt of is geen object." };
  }
  if (!Array.isArray(obj.blocks)) {
    return { ok: false, error: "Veld 'blocks' moet een array zijn." };
  }

  const blocks: Block[] = [];
  for (const [i, raw] of obj.blocks.entries()) {
    const valid = validateBlock(raw, i);
    if (!valid.ok) return { ok: false, error: valid.error };
    blocks.push(valid.block!);
  }

  const scenario: Scenario = {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    id: obj.id,
    name: obj.name,
    description: typeof obj.description === "string" ? obj.description : "",
    author: typeof obj.author === "string" ? obj.author : "",
    createdAt: typeof obj.createdAt === "string" ? obj.createdAt : new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isPublic: Boolean(obj.isPublic),
    view: validateView(obj.view as Record<string, unknown>),
    blocks,
  };
  return { ok: true, scenario };
}

function validateBlock(raw: unknown, index: number): { ok: boolean; error?: string; block?: Block } {
  if (!raw || typeof raw !== "object") {
    return { ok: false, error: `Blok ${index}: geen object.` };
  }
  const b = raw as Record<string, unknown>;
  if (typeof b.id !== "string" || !b.id) {
    return { ok: false, error: `Blok ${index}: 'id' ontbreekt.` };
  }
  if (typeof b.type !== "string" || !VALID_TYPES.includes(b.type as BlockType)) {
    return { ok: false, error: `Blok ${index}: type '${String(b.type)}' onbekend.` };
  }
  if (!Array.isArray(b.polygon) || b.polygon.length < 3) {
    return { ok: false, error: `Blok ${index}: polygoon heeft minder dan 3 hoekpunten.` };
  }
  const polygon: [number, number][] = [];
  for (const [j, p] of (b.polygon as unknown[]).entries()) {
    if (
      !Array.isArray(p) ||
      p.length < 2 ||
      typeof p[0] !== "number" ||
      typeof p[1] !== "number"
    ) {
      return { ok: false, error: `Blok ${index} hoekpunt ${j}: ongeldige coördinaat.` };
    }
    polygon.push([p[0] as number, p[1] as number]);
  }
  return {
    ok: true,
    block: {
      id: b.id,
      name: typeof b.name === "string" ? b.name : "",
      type: b.type as BlockType,
      polygon,
      heightTop: typeof b.heightTop === "number" ? b.heightTop : 0,
      heightBottom: typeof b.heightBottom === "number" ? b.heightBottom : 0,
      opacity: typeof b.opacity === "number" ? b.opacity : 60,
      lightTransmission: typeof b.lightTransmission === "number" ? b.lightTransmission : undefined,
      color: typeof b.color === "string" ? b.color : "#888888",
    },
  };
}

function validateView(raw: Record<string, unknown>): Scenario["view"] {
  return {
    centerLat: typeof raw.centerLat === "number" ? raw.centerLat : 52.2292,
    centerLng: typeof raw.centerLng === "number" ? raw.centerLng : 5.1669,
    zoom: typeof raw.zoom === "number" ? raw.zoom : 17,
    rotation: typeof raw.rotation === "number" ? raw.rotation : 0,
  };
}
